import React from 'react';
import { Button, DoughnutChart, RadioInput } from 'components';
import { CopyIcon, CopyLinkIcon } from 'assets';
import { UseFormRegister } from 'react-hook-form';
import styles from './styles.module.scss';
import { getScoreTextClass } from 'utils/scoreColor';

interface CandidateCardProps {
  candidate: {
    id: string;
    name: string;
    score: number;
    email: string;
    phone_number: string;
    created_at: string;
    portfolio: string;
    resume: string;
  };
  onSwitchCandidate?: () => void;
  register: UseFormRegister<any>;
  formFieldName: string;
  decisionValue?: string;
  decisionOptions?: { label: string; value: string }[];
  onDecisionChange?: (value: string) => void;
  className?: string;
}

const defaultDecisionOptions = [
  { label: "Revisit", value: "revisit" },
  { label: "Probe further", value: "probe_further" },
  { label: "Interview", value: "interview" },
  { label: "Reject", value: "reject" },
];

const CandidateCard: React.FC<CandidateCardProps> = ({
  candidate,
  onSwitchCandidate,
  register,
  formFieldName,
  decisionValue,
  decisionOptions,
  onDecisionChange,
  className = ""
}) => {
  return (
    <div className={`md:sticky rounded-lg border border-[#D1E9FF] p-6 shadow-sm top-0 h-fit xl:block hidden ${className}`}>
      <div className='relative w-fit mx-auto my-3 h-fit'>
        <DoughnutChart
          cutout='82%'
          score={candidate.score}
          className='text-center !h-[108px] !w-[108px]'
          borderRadius={[20, 0]}
          total={100}
        />
        <p
          className={`absolute top-[45%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-3xl font-semibold ${getScoreTextClass(candidate.score)}`}
        >
          {candidate.score}%
        </p>
        <p className='text-[#98A2B3] absolute top-[63%] left-[50%] transform -translate-x-1/2 -translate-y-1/2 font-medium text-[13px]'>avg.</p>
      </div>

      <div className='text-center mx-auto mb-5 mt-0 w-full min-[1150px]:w-[90%]'>
        <h2 className="text-[#1D2939] mb-[10px] font-medium sm:text-[19px] text-2xl">
          {candidate.name}
        </h2>

        <p className='text-[#667085] my-2 mx-0 sm:text-sm text-base break-words'>{candidate.email}</p>
        <p className='text-[#667085] mx-0 text-sm'>
          {candidate.phone_number} | {new Date(candidate.created_at).toLocaleDateString()}
        </p>
        <div className='flex justify-center gap-4 mx-0 min-[1150px]:mt-5 min-[1150px]:mb-6 my-3'>
          {candidate.portfolio && (
            <a 
              href={candidate.portfolio} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center gap-[6px] text-[#175CD3] xl:text-[0.9rem] text-sm hover:underline"
            >
              <CopyLinkIcon className="w-5 [&>path]:fill-[#175CD3] [&>path]:stroke-[0.2]" />
              Portfolio
            </a>
          )}

          {candidate.resume && (
            <a 
              href={candidate.resume} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center gap-[6px] text-[#175CD3] xl:text-[0.9rem] text-sm hover:underline"
            >
              <CopyIcon className="w-5 [&>path]:fill-[#175CD3] [&>path]:stroke-[0.2]" />
              CV
            </a>
          )}
        </div>
        <div className='border-b-[1.5px] border-[#EAECF0] w-[200px] mx-auto ' />
      </div>

      <div className="w-[86%] mx-auto md:block hidden">
        <h3 className="text-[#1a3c64] mb-[10px] font-medium text-[16px] md:w-full sm:w-[60%] mx-auto">
          Decisions
        </h3>

        <RadioInput
          name={formFieldName}
          type="radio"
          register={register}
          value={decisionValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onDecisionChange?.(e.target.value)}
          options={decisionOptions ?? defaultDecisionOptions}
          validatorMessage={""}
          parentClassName={`
            !md:mb-9 !mb-0 pt-[5px] !md:w-full !sm:w-[60%] !mx-auto

            [&>label]:w-fit
            [&>label]:mb-[5px]
            [&>label]:mt-auto
            [&>label]:border-t [&>label]:border-[#F2F4F7]
            [&>label]:pt-[8px]
            [&>label]:text-[#344054]

            [&>label>div]:flex [&>label>div]:items-center

            [&>label>div>input]:mr-[7px]
            [&>label>div>input]:w-[18px]
            [&>label>div>input]:h-[18px]
            lg:[&>label>div>input]:w-[16px]
            lg:[&>label>div>input]:h-[16px]

            [&>label>div>input:checked::after]:w-[9px]
            [&>label>div>input:checked::after]:h-[9px]
            ${styles.radioButton}
          `}
        />
        {onSwitchCandidate && (
          <Button
            onClick={onSwitchCandidate}
            type="outline"
            className="!w-full !text-xs !bg-white !py-1 !hover:bg-transparent !px-3 !border-[#D0D5DD] !text-[#344054] !mt-3"
          >
            Switch Candidate
          </Button>
        )}
      </div>

      <div className='md:hidden'>
      </div>

      {/* <Link
        to="#"
        className="flex md:hidden mx-auto mt-2 items-center gap-[6px] text-[#175CD3] xl:text-[0.9rem] text-sm hover:underline"
      >
        <MessagesIcon className="w-5 [&>path]:fill-[#175CD3] [&>path]:stroke-[0.2]" />
        Comments (0)
      </Link> */}
    </div>
  );
};

export { CandidateCard };